<script>
  import { router } from '@inertiajs/svelte'

  let { organization = {}, users_count = 0, restricted_users_count = 0, flash = {} } = $props()

  function deleteOrg() {
    if (confirm('정말 이 기관을 삭제하시겠습니까? 이 작업은 되돌릴 수 없습니다.')) {
      router.delete(`/admin/organizations/${organization.id}`)
    }
  }

  function toggleVerification() {
    const actionLabel = organization.verification_enabled ? '비활성화' : '활성화'
    if (confirm(`도메인 인증 사용을 ${actionLabel}하시겠습니까?`)) {
      router.post(`/admin/organizations/${organization.id}/toggle_verification`)
    }
  }

  function toggleLoginAccess() {
    const actionLabel = organization.login_allowed ? '제한' : '허용'
    if (confirm(`도메인 사용자의 서비스 이용을 ${actionLabel}하시겠습니까?`)) {
      router.post(`/admin/organizations/${organization.id}/toggle_login_access`)
    }
  }

  function getTypeLabel(type) {
    const labels = {
      public: '공기업',
      private: '민간기업',
      financial: '금융기관',
      government: '정부기관'
    }
    return labels[type] || type
  }

  function getTypeBadge(type) {
    switch (type) {
      case 'public': return 'badge-info'
      case 'private': return 'badge-primary'
      case 'financial': return 'badge-success'
      case 'government': return 'badge-error'
      default: return 'badge-neutral'
    }
  }
</script>

<!-- Breadcrumb -->
  <div class="flex items-center gap-2 text-caption text-content-tertiary mb-6">
    <a href="/admin" class="hover:text-primary transition-colors">관리자</a>
    <span>/</span>
    <a href="/admin/organizations" class="hover:text-primary transition-colors">기관</a>
    <span>/</span>
    <span class="text-content">{organization.name}</span>
  </div>

  <main class="max-w-4xl">
    {#if flash.notice}
      <div class="mb-6 p-4 bg-success-light dark:bg-success/20 border border-success/30 rounded-xl text-success-dark dark:text-success text-body2">
        {flash.notice}
      </div>
    {/if}

    <!-- Org Info Card -->
    <div class="card card-bordered p-6 mb-6 shadow-md">
      <div class="flex items-start justify-between mb-6">
        <div class="flex items-center gap-4">
          {#if organization.logo_url}
            <img src={organization.logo_url} alt="" class="w-16 h-16 rounded-xl object-cover" />
          {:else}
            <div class="w-16 h-16 bg-primary-50 dark:bg-dark-tertiary rounded-xl flex items-center justify-center text-primary dark:text-dark-text-high text-h1">
              {organization.name?.charAt(0) || '?'}
            </div>
          {/if}
          <div>
            <h1 class="text-h1 text-content">{organization.name}</h1>
            <p class="text-body2 text-content-tertiary font-mono">{organization.domain}</p>
          </div>
        </div>

        <div class="flex items-center gap-2">
          <a href={`/admin/organizations/${organization.id}/edit`} class="btn btn-secondary btn-sm">수정</a>
          <button onclick={toggleVerification} class="btn btn-secondary btn-sm">
            인증 {organization.verification_enabled ? '끄기' : '켜기'}
          </button>
          <button onclick={toggleLoginAccess} class={organization.login_allowed ? 'btn btn-danger btn-sm' : 'btn btn-primary btn-sm'}>
            이용 {organization.login_allowed ? '제한' : '허용'}
          </button>
          <button onclick={deleteOrg} class="btn btn-danger btn-sm">삭제</button>
        </div>
      </div>

      <div class="flex items-center gap-3 mb-6">
        <span class={`badge ${getTypeBadge(organization.org_type)}`}>{getTypeLabel(organization.org_type)}</span>
        {#if organization.auto_verify}
          <span class="badge badge-success">자동 인증 활성</span>
        {:else}
          <span class="badge badge-neutral">수동 인증</span>
        {/if}
        {#if organization.verification_enabled}
          <span class="badge badge-success">도메인 인증 사용</span>
        {:else}
          <span class="badge badge-neutral">도메인 인증 미사용</span>
        {/if}
        {#if organization.login_allowed}
          <span class="badge badge-success">서비스 이용 허용</span>
        {:else}
          <span class="badge badge-error">서비스 이용 제한</span>
        {/if}
      </div>

      <div class="grid md:grid-cols-2 gap-6">
        <div class="space-y-4">
          <h3 class="text-label text-content-tertiary uppercase">기관 정보</h3>
          <dl class="space-y-2">
            <div class="flex justify-between">
              <dt class="text-content-tertiary">기관명</dt>
              <dd class="text-content">{organization.name}</dd>
            </div>
            <div class="flex justify-between">
              <dt class="text-content-tertiary">이메일 도메인</dt>
              <dd class="text-content font-mono">{organization.domain}</dd>
            </div>
            <div class="flex justify-between">
              <dt class="text-content-tertiary">유형</dt>
              <dd class="text-content">{getTypeLabel(organization.org_type)}</dd>
            </div>
            <div class="flex justify-between">
              <dt class="text-content-tertiary">직원 수</dt>
              <dd class="text-content">{organization.employee_count ? organization.employee_count.toLocaleString() + '명' : '-'}</dd>
            </div>
          </dl>
        </div>

        <div class="space-y-4">
          <h3 class="text-label text-content-tertiary uppercase">서비스 현황</h3>
          <dl class="space-y-2">
            <div class="flex justify-between">
              <dt class="text-content-tertiary">가입 회원 수</dt>
              <dd class="text-content font-semibold">{users_count}명</dd>
            </div>
            <div class="flex justify-between">
              <dt class="text-content-tertiary">자동 인증</dt>
              <dd class="text-content">{organization.auto_verify ? '활성' : '비활성'}</dd>
            </div>
            <div class="flex justify-between">
              <dt class="text-content-tertiary">도메인 인증</dt>
              <dd class="text-content">{organization.verification_enabled ? '사용' : '미사용'}</dd>
            </div>
            <div class="flex justify-between">
              <dt class="text-content-tertiary">서비스 이용</dt>
              <dd class={organization.login_allowed ? 'text-content' : 'text-error'}>
                {organization.login_allowed ? '허용' : '제한'}
              </dd>
            </div>
            <div class="flex justify-between">
              <dt class="text-content-tertiary">이용제한 사용자 수</dt>
              <dd class="text-content font-semibold">{restricted_users_count}명</dd>
            </div>
            <div class="flex justify-between">
              <dt class="text-content-tertiary">등록일</dt>
              <dd class="text-content">{new Date(organization.created_at).toLocaleString('ko-KR')}</dd>
            </div>
          </dl>
        </div>
      </div>
    </div>

    <!-- Back -->
    <div class="flex justify-center mt-8">
      <a href="/admin/organizations" class="btn btn-secondary btn-md">목록으로</a>
    </div>
  </main>
